/**
 * ProjectTeamRoster — Read-only portrait card display of project team roles.
 *
 * Card rendering delegated to the shared TeamMemberCard component.
 *
 * Responsive: shows as many cards as fit in the container width.
 * "See all X team members" triggers onShowTeamList to open full team page.
 *
 * Used on the Core tab of the project detail page.
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiProjectRoles } from "../../api/useApiProjectRoles";
import type { ProjectRoleSlotResponse, ProjectRoleAssignmentResponse } from "../../api/projectRoles";
import { fetchAllSecurityUsers, type SecurityUserResponse } from "../../api/security";
import { mediaSrc } from "../../api/mediaUrl";
import { canSeeIdentity, type ViewerTier } from "./projectTeamTypes";
import FlipTeamMemberCard from "../TeamMemberCard/FlipTeamMemberCard";
import styles from "./ProjectTeamRoster.module.css";

interface Props {
  projectGuid: string;
  projectName: string;
  /** Viewer's permission tier for member visibility filtering. */
  viewerTier: ViewerTier;
  /** Show unfilled roles with vacancy indicators. */
  showVacant?: boolean;
  /** Called when user clicks "Manage Team" link. */
  onManageTeam?: () => void;
  /** Called when user clicks "See all X team members" to show full team page. */
  onShowTeamList?: () => void;
}

interface TeamCard {
  slot: ProjectRoleSlotResponse;
  assignment: ProjectRoleAssignmentResponse | null;
  user: SecurityUserResponse | undefined;
}

const CARD_WIDTH = 150;
const CARD_GAP = 32;

function profileImgUrl(user: SecurityUserResponse): string | null {
  return mediaSrc(user.profileImageUrl);
}

/* ── Main component ── */

const ProjectTeamRoster: React.FC<Props> = ({
  projectGuid,
  projectName,
  viewerTier,
  showVacant = false,
  onManageTeam,
  onShowTeamList,
}) => {
  const { t } = useAppTranslation("projectTeam");
  const { slots, loading } = useApiProjectRoles(projectGuid);

  // Fetch users for avatar display
  const [allUsers, setAllUsers] = useState<SecurityUserResponse[]>([]);
  useEffect(() => {
    fetchAllSecurityUsers()
      .then((users) => setAllUsers(users.filter((u) => u.isActive)))
      .catch(() => {});
  }, []);

  const userMap = useMemo(() => {
    const map = new Map<string, SecurityUserResponse>();
    allUsers.forEach((u) => map.set(u.userGuid.toUpperCase(), u));
    return map;
  }, [allUsers]);

  // ── Build display cards ──
  const cards: TeamCard[] = useMemo(() => {
    const result: TeamCard[] = [];
    const activeSlots = slots.filter(
      (s) => s.status !== "draft" && s.status !== "closed"
    );

    for (const slot of activeSlots) {
      const activeAssignments = (slot.assignments || []).filter(
        (a) => a.status === "active"
      );

      if (activeAssignments.length > 0) {
        for (const assignment of activeAssignments) {
          const user = userMap.get(assignment.userGuid.toUpperCase());
          result.push({ slot, assignment, user });
        }
      } else if (showVacant) {
        result.push({ slot, assignment: null, user: undefined });
      }
    }

    return result;
  }, [slots, userMap, showVacant]);

  // ── Responsive: calculate how many cards fit ──
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxVisible, setMaxVisible] = useState(999);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const calculate = () => {
      const w = el.clientWidth;
      const count = Math.max(1, Math.floor((w + CARD_GAP) / (CARD_WIDTH + CARD_GAP)));
      setMaxVisible(count);
    };

    calculate();
    const ro = new ResizeObserver(() => calculate());
    ro.observe(el);
    return () => ro.disconnect();
  }, [cards.length]);

  if (loading) return null;
  if (cards.length === 0) return null;

  return (
    <div className={styles.container} ref={containerRef}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.title}>{t("roster.title")}</span>
        {onManageTeam && (
          <button
            type="button"
            className={styles.manageLink}
            onClick={onManageTeam}
          >
            {t("roster.manage")}
          </button>
        )}
      </div>

      {/* Card row — shows what fits, center-justified */}
      <div className={styles.grid}>
        {cards.slice(0, maxVisible).map((item, idx) => {
          const isVacant = !item.assignment;
          const redacted =
            !!item.assignment &&
            !canSeeIdentity(item.assignment.visibility, viewerTier);
          const user = item.user;
          const displayName = user
            ? user.displayName || user.name
            : isVacant
              ? ""
              : "Unknown";
          const imgUrl = user ? profileImgUrl(user) : null;

          return (
            <FlipTeamMemberCard
              key={
                item.assignment
                  ? item.assignment.assignmentGuid
                  : `vacant-${item.slot.roleSlotGuid}-${idx}`
              }
              displayName={displayName}
              photoUrl={imgUrl}
              roleTitle={item.slot.roleLabel}
              linkedUserGuid={item.assignment?.userGuid}
              userGuid={item.assignment?.userGuid}
              isVacant={isVacant}
              isRedacted={redacted}
              splitName
              size="md"
            />
          );
        })}
      </div>

      {/* "See all" button — appears only when cards overflow */}
      {cards.length > maxVisible && onShowTeamList && (
        <button
          type="button"
          className={styles.viewAllBtn}
          onClick={onShowTeamList}
        >
          {t("roster.viewAll", { count: cards.length })}
        </button>
      )}
    </div>
  );
};

export default ProjectTeamRoster;
